<template>
    <div :class="['page page--light', { 'page--embed': partnerAppEmbedCustomization }]">
        <section class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header class="row__header row__header--margin-bottom">
                    <back-button :isAbsolute="false"
                        :isBlack="true"
                    />

                    <h1>{{ $t('marketplace.orderHistory.title') }}</h1>
                </header>

                <div class="wrapper__content">
                    <div v-if="!loading"
                        class="trading__table"
                    >
                        <div v-if="!Object.keys(tradeOrders).length"
                            class="trading__no-results"
                        >
                            {{ $t('marketplace.orderHistory.noOrdersFound') }}
                        </div>

                        <div v-for="(order, index) in tradeOrders"
                            v-else
                            :id="`row-${index}`"
                            :key="'row'+index"
                            class="trading__table-row row"
                            @click="goToOrderDetails(order)"
                        >
                            <div class="row-progress"
                                :style="{
                                    width: `${order.percentage}%`,
                                    backgroundColor: order.progressColor,
                                }"
                            />

                            <div class="row-data data flex flex--vertical-left flex--horizontal-center flex--direction-row">
                                <img v-if="order.partnerLogo"
                                    :src="order.partnerLogo"
                                    class="data__logo"
                                >
                                <div v-else
                                    class="data__logo data__logo--placeholder"
                                />

                                <div class="flex flex--horizontal-left">
                                    <label class="data__short-name">{{ order.market.symbol }}</label>
                                    <label class="data__order-price">{{ order.price }}</label>
                                </div>
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.orderHistory.tableSide'))"
                                :class="['row-data', 'flex', 'data', order.sideClass]"
                            >
                                <i :class="['data__icon', `icon-trading-order-${order.side}`]" />
                                <label class="data__content">{{ order.side }}</label>
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.orderHistory.tableDate'))"
                                class="row-data flex data"
                            >
                                <i class="data__icon icon-trading-order-date" />
                                <label class="data__content data__content--date">
                                    <span>{{ order.date }}</span>
                                    <span>{{ order.time }}</span>
                                </label>
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.orderHistory.tablePercentage'))"
                                class="row-data flex data data--hide-md"
                            >
                                <i class="data__icon icon-trading-order-progress" />
                                <progress-bar class="data_content data__content--progress"
                                    :percentage="order.percentage"
                                    :progressBarColor="order.progressColor"
                                    :showNumber="false"
                                    :customHeight="1.5"
                                />
                            </div>

                            <div v-tooltip="getTooltip($t('marketplace.orderHistory.tableAmount'))"
                                class="row-data flex data"
                            >
                                <i class="data__icon icon-trading-order-total" />
                                <label class="data__content">{{ order.amount }}</label>
                            </div>
                        </div>
                    </div>

                    <pagination class="pagination"
                        :last-page="lastPage"
                        @currentPage="fetchData"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<script>
// 3rd Party libs
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Helpers
import {
    formatSingleOrderDetails,
    mapOrdersDetails,
} from '@webapp/helpers/trading-history';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import progressBar from '@webapp/components/progress/progress-bar.vue';
import pagination from '@webapp/components/pagination/pagination.vue';

// Local Types
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';

// Mixins
import pollingMixin from '@shared/mixins/generic-polling-mixin.ts';

export default {
    name: 'trading-history',
    components: {
        backButton,
        progressBar,
        pagination,
    },
    mixins: [pollingMixin],
    data() {
        return {
            loading: true,
            tradeOrders: [],
            currentPage: 1,
        };
    },
    computed: {
        ...mapGetters(['partnerAppEmbedCustomization']),
        ...mapGetters('marketplace', [
            'userTradeOrders',
            'userTradeOrdersLastPage',
            'cancelledTradingOrder',
            'cancellableOrders',
        ]),

        ...mapGetters('partners', ['partnerByCurrencyId']),

        lastPage() {
            return this.userTradeOrdersLastPage;
        },
    },
    watch: {
        userTradeOrders(newVal) {
            this.tradeOrders = mapOrdersDetails(newVal);

            this.loading = false;

            this.showAppLoader({
                isVisible: false,
            });
        },

        cancellableOrders: {
            immediate: true,
            handler(orders) {
                if (orders.length) {
                    this.pollingMixinStartPolling(
                        false,
                        (10 * 1000),
                        () => this.fetchData(this.currentPage),
                    );
                } else {
                    this.pollingMixinTerminatePolling();
                }
            },
        },
    },
    created() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        this.initData();
    },
    methods: {
        ...mapActions({
            loadTradeOrders: localEpicTypes.GET_USER_TRADE_ORDERS,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
        }),

        ...mapActions('marketplace', {
            setCancelOrder: localStoreTypes.SET_CANCELLED_ORDER,
        }),

        getProgressColor(side) {
            switch (side) {
                case 'buy':
                    return '#ffa101';
                case 'sell':
                    return '#007aff';
                default:
                    return '';
            }
        },

        initData() {
            // Reset the cancelled order every time user checks the list
            this.setCancelOrder();
        },

        getTooltip(tooltip) {
            return {
                content: tooltip,
                delay: 200,
                placement: 'top-end',
            };
        },

        goToOrderDetails(order) {
            const partner = this.partnerByCurrencyId(order.market.baseCurrency?.id);

            this.$router.push({
                name: 'tradeOrderDetails',
                params: {
                    details: formatSingleOrderDetails(partner, order),
                },
                query: {
                    id: order.id,
                },
            });
        },

        fetchData(page) {
            this.currentPage = page;

            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            this.loadTradeOrders({
                page,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.trading {
    &__no-results {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        font-size: 1.5rem;
        color: $cyprus-color;
    }

    &__table {
        display: flex;
        flex-direction: column;
        border-radius: 1rem;
        padding: 1.7rem 1rem;
        margin: 0;
        background: none;

        @include min-width(md) {
            margin: 0 2rem;
            background: $white-color;
        }

        &-row {
            display: grid;
            grid-template-columns: 25% 15% 25% 20% 15%;
            padding: 1rem 1.1rem;
            position: relative;
            cursor: pointer;
            background: $white-color;
            margin: 1.2rem 0 1rem 0;

            &:before {
                height: 0;
            }

            &:first-child {
                border: none;
            }

            @include max-width(md) {
                grid-template-columns: 35% 15% 30% 20%;
            }

            @include min-width(md) {
                &:not(:last-child) {
                    &:before {
                        height: 0.3rem;
                        content: "";
                        width: 100%;
                        bottom: -1.2rem;
                        position: absolute;
                        margin-bottom: 0;
                        background: $concrete-color;
                    }
                }
            }

            &:hover {
                background: $hawkes-color;
                border-radius: 1rem;
            }

            .row {
                &-progress {
                    position: absolute;
                    top: 0;
                    height: 100%;
                    opacity: 10%;
                    display: block;

                    @include min-width(md) {
                        display: none;
                    }
                }

                &-data {
                    font: 600 1.5rem/2rem "Nunito";
                    color: $daintree-color;

                    &--buy {
                        color: $orange-peel-color;
                    }

                    &--sell {
                        color: $azure-radiance-color;
                    }

                    &.data {
                        &--hide-md {
                            @include max-width(md) {
                                display: none;
                            }
                        }

                        .data {
                            &__logo {
                                min-width: 5.5rem;
                                width: 5.5rem;
                                height: 5.5rem;
                                margin-right: 1rem;

                                @include max-width(sm) {
                                    min-width: 3rem;
                                    width: 3rem;
                                    height: 3rem;
                                }

                                &--placeholder {
                                    border-radius: 50%;
                                    background-color: $dusty-gray-color;
                                }
                            }

                            &__order-price {
                                font-size: 1.8rem;
                                background: $athens-gray-color;
                                padding: 0.6rem 1rem;
                                border-radius: 0.6rem;
                                margin-top: 0.2rem;
                                min-width: 7.7rem;
                                text-align: center;

                                @include max-width(sm) {
                                    font-size: 1.5rem;
                                    min-width: 5.7rem;
                                }
                            }

                            &__icon {
                                min-height: 2rem;
                                line-height: 2rem;
                            }

                            &__content {
                                min-height: 3.2rem;
                                line-height: 3.2rem;
                                text-transform: capitalize;

                                &--date {
                                    display: flex;
                                    flex-direction: column;
                                    align-items: flex-end;
                                    line-height: 1.1;
                                    font-size: 1.3rem;
                                }

                                &--progress {
                                    margin: 0.6em 0;
                                    width: 80%;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

.pagination {
    padding: 3.7rem 2rem;
}

.flex {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    flex-direction: column;

    &--horizontal-center {
        align-items: center;
    }

    &--horizontal-left {
        align-items: flex-start;
    }

    &--vertical-center {
        justify-content: center;
    }

    &--vertical-left {
        justify-content: left;
    }

    &--direction-row {
        flex-direction: row;
    }
}
</style>